<script lang="ts" setup>
    import { onBeforeUnmount, ref, watch } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import { useNavigationStore } from '@/js/stores/navigation';
    import { MessageCircleHeartIcon } from '@lucide/vue';
    import EchoTooltip from '../EchoTooltip.vue';

    // English source strings used to locate elements in the third-party iframe.
    const widgetStrings: Array<{ english: string; key: Parameters<typeof __>[0] }> = [
        { english: 'Send feedback', key: 'jsd-widget-title' },
        { english: 'Area', key: 'jsd-widget-area' },
        { english: 'Short description', key: 'jsd-widget-short-description' },
        { english: 'In-depth description', key: 'jsd-widget-indepth-description' },
        { english: 'Company name', key: 'jsd-widget-company' },
        { english: 'Select...', key: 'jsd-widget-select' },
        { english: 'Your contact e-mail', key: 'jsd-widget-email-placeholder' },
        { english: 'Thanks!', key: 'jsd-widget-thanks' },
        {
            english: 'Thank you! We read every feedback even though we cannot respond to all.',
            key: 'jsd-widget-confirmation',
        },
        { english: 'Send', key: 'jsd-widget-submit' },
        { english: 'User Interface / General', key: 'jsd-widget-area-ui' },
        { english: 'Performance', key: 'jsd-widget-area-performance' },
        { english: 'Functionality request', key: 'jsd-widget-area-functionality' },
    ];

    const navigation = useNavigationStore();
    const itemRef = ref<HTMLLIElement>();

    const isWidgetOpen = ref(false);
    let widgetObserver: ResizeObserver | undefined;
    let translationObserver: MutationObserver | undefined;
    let clickOutsideHandler: (() => void) | undefined;
    let clickListenerTimeout: ReturnType<typeof setTimeout> | undefined;
    let confirmationTimer: ReturnType<typeof setTimeout> | undefined;

    const repositionWidget = (frame: HTMLIFrameElement) => {
        const navEl = itemRef.value?.closest<HTMLElement>('[aria-label]');
        const navWidth = navEl?.getBoundingClientRect().width ?? (navigation.visible ? 288 : 64);
        frame.style.setProperty('left', `${navWidth + 8}px`, 'important');
        frame.style.setProperty('right', 'auto', 'important');
    };

    watch(
        () => navigation.visible,
        () => {
            if (!isWidgetOpen.value) return;
            const frameElement = document.getElementById('jsd-widget');
            if (frameElement instanceof HTMLIFrameElement) repositionWidget(frameElement);
        }
    );

    const hideWidget = (frame: HTMLIFrameElement) => {
        frame.style.setProperty('opacity', '0', 'important');
        frame.style.setProperty('pointer-events', 'none', 'important');
        frame.style.removeProperty('left');
        frame.style.removeProperty('right');
        isWidgetOpen.value = false;
        widgetObserver?.disconnect();
        translationObserver?.disconnect();
        clearTimeout(clickListenerTimeout);
        clearTimeout(confirmationTimer);
        clickListenerTimeout = undefined;
        confirmationTimer = undefined;
        if (clickOutsideHandler) {
            document.removeEventListener('click', clickOutsideHandler);
            clickOutsideHandler = undefined;
        }
    };

    const openFeedbackWidget = () => {
        const frame = document.getElementById('jsd-widget');
        if (!(frame instanceof HTMLIFrameElement)) return;

        if (isWidgetOpen.value) {
            hideWidget(frame);
            return;
        }

        try {
            const doc = frame.contentDocument;
            if (doc?.body) {
                if (!doc.getElementById('echo-jsd-styles')) {
                    const style = doc.createElement('style');
                    style.id = 'echo-jsd-styles';
                    style.textContent = `
                        #help-button { visibility: hidden !important; }
                        button[type="submit"] {
                            background-color: #490838 !important;
                            color: #fff !important;
                            border-radius: 9999px !important;
                            padding: 10px 24px !important;
                            font-size: 0.875rem !important;
                            font-weight: 700 !important;
                            line-height: 1 !important;
                            display: inline-flex !important;
                            align-items: center !important;
                        }
                        button[type="submit"]:hover,
                        button[type="submit"]:focus-within {
                            background-color: #6d3960 !important;
                            color: #fff !important;
                        }
                    `;
                    doc.head.appendChild(style);
                }

                const button = doc.getElementById('help-button');
                button?.click();

                const setTextNode = (el: Element, text: string) => {
                    const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT);
                    const node = walker.nextNode() as Text | null;
                    if (node) node.textContent = text;
                };

                const translate = () => {
                    for (const { english, key } of widgetStrings) {
                        const marked = doc.querySelector<HTMLElement>(`[data-jsd-key="${key}"]`);
                        if (marked) {
                            setTextNode(marked, __(key));
                            continue;
                        }
                        const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
                        let node = walker.nextNode() as Text | null;
                        let found = false;
                        while (node) {
                            if (node.textContent?.trim() === english) {
                                node.textContent = __(key);
                                node.parentElement?.setAttribute('data-jsd-key', key);
                                found = true;
                                break;
                            }
                            node = walker.nextNode() as Text | null;
                        }
                        if (!found && import.meta.env.DEV) {
                            console.warn(
                                `[JSD Widget] Could not find string: "${english}" (key: ${key})`
                            );
                        }
                        if (found && key === 'jsd-widget-confirmation') {
                            clearTimeout(confirmationTimer);
                            confirmationTimer = setTimeout(() => hideWidget(frame), 4000);
                        }
                    }
                };

                translate();
                translationObserver?.disconnect();
                translationObserver = new MutationObserver(translate);
                translationObserver.observe(doc.body, { childList: true, subtree: true });
            }
        } catch {
            // Ignore cross-origin iframe access; the widget may still open via its own UI behavior.
        }

        repositionWidget(frame);
        frame.style.setProperty('opacity', '1', 'important');
        frame.style.setProperty('pointer-events', 'auto', 'important');
        isWidgetOpen.value = true;

        let hasSeenOpen = false;
        widgetObserver?.disconnect();
        widgetObserver = new ResizeObserver(([{ contentRect }]) => {
            if (contentRect.height >= 100) hasSeenOpen = true;
            if (hasSeenOpen && contentRect.height < 100) hideWidget(frame);
        });
        widgetObserver.observe(frame);

        clickListenerTimeout = setTimeout(() => {
            clickListenerTimeout = undefined;
            if (!isWidgetOpen.value) return;
            clickOutsideHandler = () => hideWidget(frame);
            document.addEventListener('click', clickOutsideHandler, { once: true });
        }, 0);
    };

    onBeforeUnmount(() => {
        widgetObserver?.disconnect();
        translationObserver?.disconnect();
        clearTimeout(clickListenerTimeout);
        clearTimeout(confirmationTimer);
        if (clickOutsideHandler) document.removeEventListener('click', clickOutsideHandler);
    });
</script>

<template>
    <li ref="itemRef">
        <EchoTooltip
            :label="__('navigation-leave-feedback')"
            position="right"
            :disabled="navigation.visible"
        >
            <button
                class="flex items-center gap-3 custom-focus rounded-sm px-2.5 py-2 hover:bg-gray-lightest text-gray-dark w-full cursor-pointer"
                @click="openFeedbackWidget"
            >
                <div class="self-start">
                    <MessageCircleHeartIcon class="size-5" />
                </div>
                <span v-if="navigation.visible">{{ __('navigation-leave-feedback') }}</span>
            </button>
        </EchoTooltip>
    </li>
</template>
